import React, { useRef, useState, useEffect } from "react";
import { useAppDispatch } from "index";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  keyframes,
  Typography,
  useTheme,
} from "@mui/material";
import { dropFile } from "@store/fileParsing/actions";
import useLanguageHook from "containers/hooks/useLanguageHook";

const pulsating = keyframes`
  0% {
    opacity: 0.9,
  }
  50% {
    opacity: 1,
  }
  100% {
    opacity: 0.9,
  }
`;

// React's state doesn't play nice with event listeners, so this variable *has* to be outside of the component, otherwise the value will not be properly updated
let confirmationResolver: (value: unknown) => void = () => {
  /* */
};

function DragAndDrop(props: { children: React.ReactNode }) {
  const [drag, setDrag] = useState(false);
  const [, setCounter] = useState(0);
  const dropRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useLanguageHook();
  const kinds = ["application/zip", "application/octet-stream", "application/x-zip-compressed"];

  const [openFileSizeAlert, setOpenFileSizeAlert] = React.useState(false);

  const handleFileSizeAlertOpen = () => {
    setOpenFileSizeAlert(true);
  };

  const handleFileSizeAlertClose = () => {
    setOpenFileSizeAlert(false);
  };

  function handleFileSizeDecision(decision: boolean) {
    confirmationResolver(decision);
    handleFileSizeAlertClose();
  }

  function confirmFileSizeAndImport(files: File[]) {
    return new Promise((resolve) => {
      // 500 MB
      if (files.some((file) => file.size > 500 * 1024 * 1024)) {
        confirmationResolver = resolve;
        console.log("Large file size detected, opening confirmation dialog");
        handleFileSizeAlertOpen();
      } else {
        resolve(true);
      }
    });
  }

  const handleDrag = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e: DragEvent) => {
    e.preventDefault();
    if (
      e.dataTransfer &&
      e.dataTransfer.items &&
      e.dataTransfer.items.length > 0 &&
      kinds.includes(e.dataTransfer.items[0].type)
    ) {
      setDrag(true);
      // Fix: cant access React State scope inside a eventListener,
      // but calling setState with a func lets us access the scope.
      setCounter((x) => x + 1);
      e.stopPropagation();
    }
  };

  const handleDragOut = (e: DragEvent) => {
    e.preventDefault();
    setCounter((x) => {
      if (x - 1 === 0) {
        setDrag(false);
        e.stopPropagation();
      }
      return x - 1;
    });
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    setCounter(0);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesToImport: File[] = Array.from(e.dataTransfer.files);
      const doImport = await confirmFileSizeAndImport(filesToImport);
      if (doImport) {
        dispatch(dropFile(filesToImport));
      }
      //e.dataTransfer.clearData();
    }
  };

  useEffect(() => {
    // Initial attachment of listeners
    if (dropRef.current) {
      dropRef.current.addEventListener("dragenter", handleDragIn);
      dropRef.current.addEventListener("dragleave", handleDragOut);
      dropRef.current.addEventListener("dragover", handleDrag);
      dropRef.current.addEventListener("drop", handleDrop);
    }

    // Upon unmount remove listeners
    return () => {
      if (dropRef.current) {
        dropRef.current.removeEventListener("dragenter", handleDragIn);
        dropRef.current.removeEventListener("dragleave", handleDragOut);
        dropRef.current.removeEventListener("dragover", handleDrag);
        dropRef.current.removeEventListener("drop", handleDrop);
      }
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
        ref={dropRef}
      >
        {drag && (
          <Box>
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                opacity: 0,
                position: "fixed",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: theme.zIndex.tooltip + 1,
                animation: `${pulsating} 2500ms linear 0ms infinite`,
              }}
            />
            <Box
              sx={{
                position: "fixed",
                top: theme.spacing(4),
                bottom: theme.spacing(4),
                left: theme.spacing(4),
                right: theme.spacing(4),
                zIndex: theme.zIndex.tooltip + 1,
                backgroundColor: theme.palette.primary.main,
                opacity: 0.7,
                borderColor: theme.palette.getContrastText(theme.palette.background.default),
                borderStyle: "dashed",
                borderWidth: "3px",
                borderRadius: "2px",
              }}
            />
            <Box
              sx={{
                position: "fixed",
                top: "50%",
                right: 0,
                left: 0,
                textAlign: "center",
                zIndex: theme.zIndex.tooltip + 1,
                textShadow: theme.shadows[1],
                color: theme.palette.getContrastText(theme.palette.background.default),
              }}
            >
              <Typography variant="h1">{t.Dropzone.Drop}</Typography>
            </Box>
          </Box>
        )}
        {props.children}
      </Box>

      <Dialog
        open={openFileSizeAlert}
        onClose={handleFileSizeAlertClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t.Dropzone.WarningLargeFileTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{t.Dropzone.WarningLargeFile}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button size="large" color="success" sx={{}} onClick={() => handleFileSizeDecision(false)}>
            {t.Dropzone.AbortImport}
          </Button>
          <Button size="large" color="warning" sx={{}} onClick={() => handleFileSizeDecision(true)}>
            {t.Dropzone.ConfirmImport}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DragAndDrop;
